/* eslint-disable max-lines */

import React, { useState, useContext, useEffect } from 'react';
import _ from 'lodash';
import KeyboardEventHandler from 'react-keyboard-event-handler';
import { withRouter } from 'react-router-dom';
import AppContext from '../../AppContext';
import {
  BarcodeIcon,
  WhiteModal,
  Paragraph,
  RedParagraph,
  SaveSpaceForParagraph,
  FlexBox,
  PrimaryButton,
  EmptyState,
  SpacingBox,
  DebouncedGreyInvertedBigInput,
  H2,
  InfoParagraph,
} from '../../Components';
import { useModal } from '../../utils/effects';
import { routes } from '../../routes';
import { onChange, onSelectNumber } from './actions';
import { translate } from './translate';
import { startBarcodeScanner, stopBarcodeScanner } from './barcodeScanner';
import { getCurrentOrder } from '../../selectors';
import { isOrderEditable } from '../../utils/order';
import { WithProductsSearchWithInput } from '../WithProductsSearch';
import ProductSearchPreview from '../ProductSearchPreview';

const modalStyles = {
  width: '80vw',
  maxWidth: 975,
  position: 'relative',
  top: '40px',
  bottom: '50px',
  maxHeight: '85vh',
  overflow: 'auto',
};

const EanSearchInput = (props) => (
  <div width="200px">
    <Paragraph>{translate('label')}</Paragraph>
    <Paragraph>{props.inputValue}</Paragraph>
    <SaveSpaceForParagraph value={props.validation}>
      <RedParagraph>{props.validation}</RedParagraph>
    </SaveSpaceForParagraph>
  </div>
);

const Video = ({ onScan }) => (
  <div>
    <video
      id="video"
      style={{
        border: '1px solid gray',
        width: 253,
        height: 253,
      }}
    />
    <SpacingBox marginTop="sm">
      <PrimaryButton
        onClick={() => startBarcodeScanner(onScan)}
        text={translate('scanUsingCamera')}
      />
    </SpacingBox>
  </div>
);

export const BarcodeScanner = withRouter(({ history }) => {
  const { openModal, closeModal, isOpen } = useModal();
  const [scanWithHiddenModal, setScanWithHiddenModal] = useState();
  const [inputValue, setInputValue] = useState(null);
  const [scanValue, setScanValue] = useState(null);
  const [validation, setValidation] = useState(null);
  const [virtualCartProductNumbers, setVirtualCartProductNumbers] = useState(
    [],
  );

  const context = useContext(AppContext);

  const handleChange = onChange({
    setInputValue,
    setValidation,
    context,
    virtualCartProductNumbers,
    setVirtualCartProductNumbers,
    history,
  });

  useEffect(() => {
    if (!isOpen) stopBarcodeScanner();
  }, [isOpen]);

  useEffect(() => {
    if (scanValue && scanValue.text) {
      handleChange(scanValue.text);
      setScanValue(null);
    }
  }, [scanValue, handleChange]);

  const selectProduct = onSelectNumber({
    context,
    history,
  });

  const currentOrder = getCurrentOrder(context);
  const isCurrentOrderEditable = isOrderEditable(_.get(currentOrder, 'doc'));

  const checkIfInputShouldBeDefocused = () => {
    const now = new Date();
    if (!window.lastEntry) {
      return;
    }
    if (now - 30*1000 > window.lastEntry) {
      if (window.eanRef) {
        if (window.barcodeOpen === false) {
          console.log('have ref but barcodeOpen is false, ignoring');
          return;
        }
        window.eanRef.blur();
        console.log('blur');
      }
      // console.log('should defocus');
    }
  };

  const openScannerWithoutModal = () => {
    if (scanWithHiddenModal) {
      setScanWithHiddenModal(false);
      stopBarcodeScanner();
      window.barcodeOpen = false;
      // console.log('stopping barcode logic');
      clearInterval(window.barCodeIntervalId);
    } else {
      setScanWithHiddenModal(true);
      startBarcodeScanner(setScanValue);
      window.barcodeOpen = true;
      // console.log('starting barcode logic');
      window.barCodeIntervalId = setInterval(checkIfInputShouldBeDefocused, 500);
    }
  };

  return (
    <>
      <SpacingBox marginRight="sm">
        <div
          style={{
		  cursor: 'pointer', fontWeight: 'bold', userSelect: 'none', minHeight: '48px', lineHeight: '48px', color: scanWithHiddenModal ? 'green' : 'black',
          }}
          onClick={openScannerWithoutModal}
        >
          {scanWithHiddenModal ? translate('scanning') : translate('scan')}
        </div>
      </SpacingBox>
      <BarcodeIcon onClick={openModal} />

      <div>
        {(scanWithHiddenModal || isOpen) && isCurrentOrderEditable && (
          <KeyboardEventHandler
            handleFocusableElements
            handleKeys={['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']}
            onKeyEvent={(key) => {
              const nextValue = (inputValue || '') + key;
              setInputValue(nextValue.slice(-13));
              handleChange(nextValue.slice(-13));
            }}
          />
        )}
      </div>
      <WhiteModal style={modalStyles} isOpen={isOpen} onCancel={closeModal}>
        {!isCurrentOrderEditable && (
          <FlexBox
            padding="sm"
            flex={1}
            flexDirection="column"
            alignItems="center">
            <EmptyState
              title={translate('emptyState.title')}
              description={translate('emptyState.description')}
            />
            <PrimaryButton
              onClick={() => {
                closeModal();
                history.push(routes.orderCustomerSelection);
              }}
              text={translate('emptyState.button')}
            />
          </FlexBox>
        )}
        {isCurrentOrderEditable && (
          <FlexBox flexDirection="column" flex={1}>
            <FlexBox
              flex={1}
              paddingTop="md"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              marginTop="md">
              <H2>{translate('scanner')}</H2>
              <InfoParagraph>{translate('scannerDescription')}</InfoParagraph>
            </FlexBox>
            <FlexBox
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              paddingTop="sm"
            >
              <div style={{
                borderRight: '1px solid rgb(152,152,152)',
              }}>
                <FlexBox
                  flexDirection="column"
                  style={{ paddingRight: '3vw' }}
                >
                  <Video onScan={setScanValue} />
                </FlexBox>
              </div>

              <FlexBox
                flexDirection="column"
                style={{ paddingLeft: '3vw' }}
              >
                <EanSearchInput
                  inputValue={inputValue}
                  validation={validation}
                  handleChange={handleChange}
                />
              </FlexBox>
            </FlexBox>
            <FlexBox
              flex={1}
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              marginTop="md">
              <H2>{translate('searchAProduct')}</H2>
              <InfoParagraph>
                {translate('searchAProductDescription')}
              </InfoParagraph>
            </FlexBox>
            <FlexBox
              flexDirection="column"
              className="searchBox"
              style={{ minHeight: 400, paddingLeft: '5vw', paddingRight: '5vw' }}>
              <WithProductsSearchWithInput
                defaultSearch=""
                page={1}
                renderInputComponent={({ search, handleSearch }) => (
                  <DebouncedGreyInvertedBigInput
                    placeholder="Search"
                    onChange={handleSearch}
                    value={search}
                  />
                )}>
                {({ products, search, setSearch }) => (
                  <SpacingBox>
                    <ProductSearchPreview
                      products={products}
                      search={search}
                      onClick={(number) => {
                        const searchInput = document.querySelector('.searchBox input');
                        searchInput.focus();
                        selectProduct(number);
                        setSearch('');
                      }}
                      onRouteToAllResults={null}
                      totalDisplayed={60}
                    />
                  </SpacingBox>
                )}
              </WithProductsSearchWithInput>
            </FlexBox>
          </FlexBox>
        )}
      </WhiteModal>
    </>
  );
});
